/**
 * FSRConfig — AMD FidelityFX Super Resolution 1.0 (spatial-only).
 *
 * Two passes:
 *   - **EASU** (Edge-Adaptive Spatial Upsampling) — 12-tap edge-aware
 *     upsample that preserves fine detail and sharp edges.
 *   - **RCAS** (Robust Contrast Adaptive Sharpening) — 3×3 unsharp with
 *     min/max neighborhood-clamped sharpness.
 *
 * v1 ships:
 *   - EASU + RCAS as composable TSL nodes
 *   - 4 quality presets matching AMD's published tiers (1.3×, 1.5×, 1.7×, 2.0×)
 *   - Mutually exclusive with TAAU (warning if both enabled)
 *
 * v2 roadmap:
 *   - FSR 2 temporal accumulation (overlaps with our existing TAAU)
 *   - FSR 3 frame generation (needs display timing API)
 *   - Reactive mask (engine-wide alpha-channel rewrite)
 *   - FP16 path (when WebGPU shader-f16 is broadly available)
 */

export type FSRQuality = 'ultra' | 'quality' | 'balanced' | 'performance';

export interface FSRQualityPreset {
  /** Render-resolution / output-resolution scale factor. */
  scaleFactor: number;
  /** Default RCAS sharpness intensity (0..1). */
  defaultSharpness: number;
}

export const FSR_PRESETS: Record<FSRQuality, FSRQualityPreset> = {
  ultra:       { scaleFactor: 1.3, defaultSharpness: 0.20 },
  quality:     { scaleFactor: 1.5, defaultSharpness: 0.25 },
  balanced:    { scaleFactor: 1.7, defaultSharpness: 0.30 },
  performance: { scaleFactor: 2.0, defaultSharpness: 0.35 },
};

export interface FSRConfig {
  /** Quality preset. */
  quality: FSRQuality;
  /**
   * RCAS sharpness, 0..1. Stronger = more crisp but amplifies noise.
   * Default driven by quality preset.
   */
  sharpness: number;
  /** Enable RCAS sharpening pass. Default true. */
  enableSharpening: boolean;
}

export function createFSRConfig(overrides: Partial<FSRConfig> = {}): FSRConfig {
  const quality = overrides.quality ?? 'quality';
  const preset = FSR_PRESETS[quality];
  return {
    quality,
    sharpness: overrides.sharpness ?? preset.defaultSharpness,
    enableSharpening: overrides.enableSharpening ?? true,
  };
}

// ─── CPU reference helpers (tested) ─────────────────────────────────────────

/**
 * Compute the input render resolution for a given output and quality
 * preset. Returns integer pixel dimensions.
 */
export function fsrRenderResolution(
  outputWidth: number, outputHeight: number, quality: FSRQuality,
): { width: number; height: number } {
  const scale = FSR_PRESETS[quality].scaleFactor;
  return {
    width: Math.max(1, Math.round(outputWidth / scale)),
    height: Math.max(1, Math.round(outputHeight / scale)),
  };
}

/**
 * EASU edge direction estimator (CPU reference).
 *
 * Given 4 surrounding samples (NW, NE, SW, SE) brightness values, returns
 * the principal edge direction as a 2D unit vector (in pixel space).
 *
 * Used by tests and as documentation for the GPU EASU kernel; the GPU
 * version computes this from the 12-tap pattern but the gist is the same.
 */
export function easuDirectionEstimate(
  nw: number, ne: number, sw: number, se: number,
): { dx: number; dy: number; len: number } {
  // Horizontal edge magnitude: difference between top + bottom row averages
  const horizDiff = (nw + ne) - (sw + se);
  // Vertical edge magnitude: difference between left + right col averages
  const vertDiff = (nw + sw) - (ne + se);
  // Direction perpendicular to gradient
  const dx = -vertDiff;
  const dy = horizDiff;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) return { dx: 0, dy: 0, len: 0 };
  return { dx: dx / len, dy: dy / len, len };
}

/**
 * RCAS sharpening weight (CPU reference).
 *
 * Given the center luma + the 4 neighbor lumas (N, S, E, W), returns the
 * "lobe" weight to apply to the neighbor sum during the unsharp filter.
 * Negative lobe values produce sharpening.
 *
 *   minRGB = min(min(min(N, S), E), W)
 *   maxRGB = max(max(max(N, S), E), W)
 *   hitMin = min(minRGB, c) / maxRGB
 *   hitMax = (1 - max(maxRGB, c)) / (1 - minRGB)
 *   lobe = max(-hitMin, hitMax) * (-sharpness)
 *
 * Operates on luminance for simplicity here; the shader operates per-channel.
 */
export function rcasLobeWeight(
  c: number, n: number, s: number, e: number, w: number, sharpness: number,
): number {
  const minRGB = Math.min(n, s, e, w);
  const maxRGB = Math.max(n, s, e, w);
  if (maxRGB === 0) return 0;
  const hitMin = Math.min(minRGB, c) / maxRGB;
  const hitMax = (1 - Math.max(maxRGB, c)) / Math.max(1 - minRGB, 1e-6);
  return Math.max(-hitMin, hitMax) * (-sharpness);
}
